import { useEffect } from "react";
import type { CameraMediaSinkRegistry } from "../../app/display/media_sink_registry";
import { setPendingRemoteAudioStream } from "./remote_audio_registry";
import {
  isConfiguredVehicleAudioMid,
  resolveCameraTrackSlot,
} from "../../interface/webrtc_media_slots";
import type { CameraTrackSlot } from "../../interface/webrtc_media_slots";
import SocketInterface from "../../interface/socket_interface";
import {
  isRemoteClientDebugEnabled,
  logRemoteClientDebug,
  logRemoteClientEvent,
} from "../../utils/remoteClient/remoteClientLogger";

interface UseRemoteMediaTracksParams {
  socket: SocketInterface | null;
  config: any;
  mediaSinkRegistry: CameraMediaSinkRegistry;
  startReceiverFpsMonitor: (
    slot: CameraTrackSlot,
    receiver: RTCRtpReceiver,
  ) => void;
  stopReceiverFpsMonitor: (slot: CameraTrackSlot) => void;
}

// 차량→콕핏 오디오 수신 진단 — 항상 켜짐(디버그 플래그 무관). 실차 콕핏 devtools 에서
// 운영자가 직접 읽어, 오디오가 도착하는지(차량 송신) vs 로컬에서 막혔는지(자동재생 차단)
// 구분할 수 있도록 inbound-rtp audio 의 packetsReceived/bytesReceived 를 샘플링한다.
async function logAudioReceiverStats(
  receiver: RTCRtpReceiver | undefined,
  label: string,
): Promise<void> {
  if (!receiver?.getStats) {
    return;
  }

  try {
    const stats = await receiver.getStats();
    let inbound: Record<string, any> | null = null;
    let track: Record<string, any> | null = null;

    stats.forEach((report: any) => {
      if (report.type === "inbound-rtp" && report.kind === "audio") {
        inbound = report;
      } else if (report.type === "track" && report.kind === "audio") {
        track = report;
      }
    });

    console.log(`[AdRemoteClient][AUDIO] Receiver stats (${label})`, {
      inbound: inbound
        ? {
            packetsReceived: (inbound as any).packetsReceived ?? null,
            bytesReceived: (inbound as any).bytesReceived ?? null,
            packetsLost: (inbound as any).packetsLost ?? null,
            jitter: (inbound as any).jitter ?? null,
            audioLevel: (inbound as any).audioLevel ?? null,
            totalAudioEnergy: (inbound as any).totalAudioEnergy ?? null,
            codecId: (inbound as any).codecId ?? null,
          }
        : null,
      track: track
        ? {
            audioLevel: (track as any).audioLevel ?? null,
            totalAudioEnergy: (track as any).totalAudioEnergy ?? null,
            ended: (track as any).ended ?? null,
          }
        : null,
    });
  } catch (err) {
    console.warn("[AdRemoteClient][AUDIO] Receiver stats failed", {
      label,
      reason: err instanceof Error ? err.message : String(err),
    });
  }
}

async function logReceiverStats(
  receiver: RTCRtpReceiver | undefined,
  label: string,
): Promise<void> {
  if (!receiver?.getStats || !isRemoteClientDebugEnabled()) {
    return;
  }

  try {
    const stats = await receiver.getStats();
    let inbound: Record<string, any> | null = null;
    let track: Record<string, any> | null = null;
    let transport: Record<string, any> | null = null;
    let candidatePair: Record<string, any> | null = null;

    stats.forEach((report: any) => {
      if (report.type === "inbound-rtp" && report.kind === "video") {
        inbound = report;
      } else if (report.type === "track" && report.kind === "video") {
        track = report;
      } else if (report.type === "transport") {
        transport = report;
      } else if (
        report.type === "candidate-pair" &&
        (report.nominated || report.selected)
      ) {
        candidatePair = report;
      }
    });

    logRemoteClientDebug(`Receiver stats (${label})`, {
      inbound: inbound
        ? {
            bytesReceived: inbound.bytesReceived ?? null,
            packetsReceived: inbound.packetsReceived ?? null,
            framesDecoded: inbound.framesDecoded ?? null,
            framesReceived: inbound.framesReceived ?? null,
            keyFramesDecoded: inbound.keyFramesDecoded ?? null,
            jitter: inbound.jitter ?? null,
            codecId: inbound.codecId ?? null,
          }
        : null,
      track: track
        ? {
            framesDecoded: track.framesDecoded ?? null,
            framesReceived: track.framesReceived ?? null,
            frameWidth: track.frameWidth ?? null,
            frameHeight: track.frameHeight ?? null,
          }
        : null,
      transport: transport
        ? {
            dtlsState: transport.dtlsState ?? null,
            iceState: transport.iceState ?? null,
            selectedCandidatePairId: transport.selectedCandidatePairId ?? null,
          }
        : null,
      candidatePair: candidatePair
        ? {
            state: candidatePair.state ?? null,
            localCandidateId: candidatePair.localCandidateId ?? null,
            remoteCandidateId: candidatePair.remoteCandidateId ?? null,
            bytesReceived: candidatePair.bytesReceived ?? null,
            bytesSent: candidatePair.bytesSent ?? null,
            currentRoundTripTime: candidatePair.currentRoundTripTime ?? null,
          }
        : null,
    });
  } catch (err) {
    console.warn("[AdRemoteClient] Receiver stats failed", {
      label,
      reason: err instanceof Error ? err.message : String(err),
    });
  }
}

const attachMediaStream = (
  elementId: string,
  stream: MediaStream,
  isAudio: boolean = false,
) => {
  const element = document.getElementById(elementId) as HTMLMediaElement | null;
  if (!element) {
    console.warn("[AdRemoteClient] Media element not found", {
      elementId,
      isAudio,
      streamId: stream?.id ?? null,
      trackIds: stream?.getTracks?.().map((track) => track.id) ?? [],
    });
    return;
  }

  // 스피커 깜빡임(잡혔다 안잡혔다) 방지 — onTrack 은 재협상/중복으로 같은 오디오 트랙에 대해
  // 여러 번 발화하고, createStreamFromTrack 은 매번 새 MediaStream 래퍼를 만든다. srcObject 를
  // 매번 재할당하면 재생이 끊겼다 다시 시작돼 무음/끊김이 반복된다. 이미 "같은 라이브 오디오
  // 트랙"이 붙어 재생 중이면 재할당을 건너뛴다(트랙 id 로 동일성 판단 — 래퍼가 달라도 안전).
  if (isAudio) {
    const current = element.srcObject as MediaStream | null;
    const nextTrack = stream?.getAudioTracks?.()[0] || null;
    const currentTrack = current?.getAudioTracks?.()[0] || null;
    if (
      nextTrack &&
      currentTrack &&
      currentTrack.id === nextTrack.id &&
      currentTrack.readyState === "live" &&
      !element.paused
    ) {
      (element as HTMLAudioElement).muted = false;
      return; // 동일 라이브 트랙 재생 중 — 재부착 생략(깜빡임 방지).
    }
  }

  element.onloadedmetadata = () => {
    logRemoteClientDebug("Media metadata loaded", {
      elementId,
      readyState: element.readyState,
      paused: element.paused,
      currentTime: element.currentTime,
      videoWidth: (element as HTMLVideoElement).videoWidth ?? null,
      videoHeight: (element as HTMLVideoElement).videoHeight ?? null,
    });
  };
  element.oncanplay = () => {
    logRemoteClientDebug("Media can play", {
      elementId,
      readyState: element.readyState,
      videoWidth: (element as HTMLVideoElement).videoWidth ?? null,
      videoHeight: (element as HTMLVideoElement).videoHeight ?? null,
    });
  };
  element.onplaying = () => {
    logRemoteClientDebug("Media playing", {
      elementId,
      readyState: element.readyState,
      currentTime: element.currentTime,
      videoWidth: (element as HTMLVideoElement).videoWidth ?? null,
      videoHeight: (element as HTMLVideoElement).videoHeight ?? null,
    });
  };
  element.onwaiting = () => {
    console.warn("[AdRemoteClient] Media waiting", {
      elementId,
      readyState: element.readyState,
      currentTime: element.currentTime,
      videoWidth: (element as HTMLVideoElement).videoWidth ?? null,
      videoHeight: (element as HTMLVideoElement).videoHeight ?? null,
    });
  };
  element.onstalled = () => {
    console.warn("[AdRemoteClient] Media stalled", {
      elementId,
      readyState: element.readyState,
      currentTime: element.currentTime,
    });
  };
  element.onerror = () => {
    console.error("[AdRemoteClient] Media element error", {
      elementId,
      code: element.error?.code ?? null,
      message: element.error?.message || null,
    });
  };

  element.srcObject = stream;
  logRemoteClientDebug("Attaching media stream", {
    elementId,
    isAudio,
    streamId: stream?.id ?? null,
    active: stream?.active ?? null,
    trackIds: stream?.getTracks?.().map((track) => track.id) ?? [],
  });

  if (isAudio) {
    // 항상 켜진 진단(실차 콕핏 devtools 용) — 오디오 트랙이 #remote-audio 에 연결되는 순간 기록.
    console.log("[AdRemoteClient][AUDIO] Attaching audio track to #remote-audio", {
      elementId,
      streamId: stream?.id ?? null,
      active: stream?.active ?? null,
      trackIds: stream?.getAudioTracks?.().map((track) => track.id) ?? [],
    });
    // develop 동작과 동일: 즉시 unmute 후 play(). (운영 환경은 로그인 클릭의 sticky activation
    // 으로 자동재생이 허용되어 그대로 소리가 난다. 과거의 muted-then-unmute/keep-alive 우회는
    // 오히려 unmute 직후 재일시정지를 유발해 무음이 되던 회귀의 원인이라 제거함.)
    (element as HTMLAudioElement).muted = false;
  }

  const playResult = element.play() as unknown;
  if (playResult && typeof (playResult as Promise<void>).catch === "function") {
    (playResult as Promise<void>).catch((err) => {
      console.error("[AdRemoteClient] element.play() failed", {
        elementId,
        isAudio,
        reason: err instanceof Error ? err.message : String(err),
      });
    });
  }
};

const createStreamFromTrack = (track: MediaStreamTrack): MediaStream => {
  if (typeof MediaStream !== "undefined") {
    try {
      return new MediaStream([track]);
    } catch (_err) {
      // Test environments may provide non-native track stubs.
    }
  }

  return {
    getTracks: () => [track],
  } as MediaStream;
};

export default function useRemoteMediaTracks({
  socket,
  config,
  mediaSinkRegistry,
  startReceiverFpsMonitor,
  stopReceiverFpsMonitor,
}: UseRemoteMediaTracksParams) {
  useEffect(() => {
    if (!socket) return;

    const onTrackHandler = (msg: any) => {
      logRemoteClientEvent("onTrackHandler", msg);
      const mid = String(msg?.transceiver?.mid || "");
      const track = msg?.track || msg?.streams?.[0]?.getTracks?.()[0] || null;

      // 항상 켜진 진단(실차 콕핏 devtools 용) — ontrack 으로 들어오는 모든 트랙의
      // kind/mid/transceiver direction 을 남겨, 차량이 오디오 m-line 을 보내는지
      // (recvonly 로 수신 중인지) 즉시 확인할 수 있게 한다.
      if (track?.kind === "audio") {
        console.log("[AdRemoteClient][AUDIO] ontrack received audio track", {
          mid: mid || null,
          trackId: track?.id ?? null,
          trackMuted: track?.muted ?? null,
          trackReadyState: track?.readyState ?? null,
          transceiverDirection: msg?.transceiver?.direction ?? null,
          transceiverCurrentDirection: msg?.transceiver?.currentDirection ?? null,
          streamId: msg?.streams?.[0]?.id ?? null,
        });
      }
      const stream = track
        ? createStreamFromTrack(track)
        : msg?.streams?.[0] || null;
      if (!stream) {
        return;
      }

      const cameraSlot =
        resolveCameraTrackSlot(mid, config) ||
        resolveCameraTrackSlot(msg?.cameraSlot, config);

      logRemoteClientDebug("Remote track received", {
        mid: mid || null,
        trackKind: track?.kind ?? null,
        trackId: track?.id ?? null,
        trackMuted: track?.muted ?? null,
        trackReadyState: track?.readyState ?? null,
        streamId: msg?.streams?.[0]?.id ?? null,
        streamActive: msg?.streams?.[0]?.active ?? null,
        cameraSlot: cameraSlot ?? null,
      });

      if (!cameraSlot && track?.kind === "video") {
        console.warn("[AdRemoteClient] Ignoring remote video track without a resolvable camera slot", {
          mid: mid || null,
          cameraSlot: msg?.cameraSlot ?? null,
          trackId: track?.id ?? null,
          streamId: msg?.streams?.[0]?.id ?? null,
        });
        return;
      }

      if (track) {
        track.onmute = () => {
          console.warn("[AdRemoteClient] Remote track muted", {
            mid: mid || null,
            trackId: track.id,
            kind: track.kind,
            readyState: track.readyState,
          });
        };
        track.onunmute = () => {
          logRemoteClientDebug("Remote track unmuted", {
            mid: mid || null,
            trackId: track.id,
            kind: track.kind,
            readyState: track.readyState,
          });
        };
        track.onended = () => {
          console.warn("[AdRemoteClient] Remote track ended", {
            mid: mid || null,
            trackId: track.id,
            kind: track.kind,
            readyState: track.readyState,
          });
          if (cameraSlot) {
            stopReceiverFpsMonitor(cameraSlot);
          }
        };
      }

      const receiver = msg?.receiver as RTCRtpReceiver | undefined;
      if (track?.kind === "video" && receiver?.getStats) {
        void logReceiverStats(receiver, `${mid || cameraSlot || "unknown"}@0ms`);
        window.setTimeout(() => {
          void logReceiverStats(receiver, `${mid || cameraSlot || "unknown"}@1000ms`);
        }, 1000);
        window.setTimeout(() => {
          void logReceiverStats(receiver, `${mid || cameraSlot || "unknown"}@3000ms`);
        }, 3000);
      }

      // 항상 켜진 오디오 수신 샘플러(실차 콕핏 devtools 용) — ~1s/~3s 에 inbound-rtp
      // audio packetsReceived/bytesReceived 를 찍어, 차량이 실제로 오디오를 보내고
      // 있는지(수신 증가) vs 로컬 자동재생만 막혔는지 판별할 수 있게 한다.
      if (track?.kind === "audio" && receiver?.getStats) {
        const audioLabel = `audio:${mid || "unknown"}`;
        void logAudioReceiverStats(receiver, `${audioLabel}@0ms`);
        window.setTimeout(() => {
          void logAudioReceiverStats(receiver, `${audioLabel}@1000ms`);
        }, 1000);
        window.setTimeout(() => {
          void logAudioReceiverStats(receiver, `${audioLabel}@3000ms`);
        }, 3000);
      }

      // 오디오 판별을 "넓게" 한다 — 실제 차량 오디오는 track.kind 없이 stream + 오디오
      // mid("mic_audio")로만 들어오는 경우가 있어, track.kind==="audio" 만 검사하면 누락되어
      // 스피커가 무음이 되는 회귀가 반복됐다. 다음 중 하나면 #remote-audio 로 연결한다:
      //   (1) 명시적 오디오 트랙, (2) 설정된 차량 오디오 mid, (3) cameraSlot 이 아닌데 stream 에
      //   오디오 트랙이 있는 경우. (카메라 슬롯으로 오인되어 비디오 싱크로 빠지는 것도 차단.)
      const streamHasAudioTrack =
        (stream?.getAudioTracks?.()?.length ?? 0) > 0;
      const looksLikeAudio =
        track?.kind === "audio" ||
        isConfiguredVehicleAudioMid(mid, config) ||
        (!cameraSlot && streamHasAudioTrack);
      if (looksLikeAudio) {
        console.log("[AdRemoteClient][AUDIO] routing audio → #remote-audio", {
          mid: mid || null,
          cameraSlot: cameraSlot ?? null,
          trackKind: track?.kind ?? null,
          configuredAudioMid: isConfiguredVehicleAudioMid(mid, config),
          streamHasAudioTrack,
        });
        // 최신 수신 오디오 스트림을 보관(타이밍 버그 안전망) — onTrack 이 #remote-audio
        // 마운트보다 먼저 발화해 attach 가 실패해도, Communicator 워치독이 마운트 후
        // 이 보관 스트림으로 재부착한다. [[remote_audio_registry]]
        setPendingRemoteAudioStream(stream);
        attachMediaStream("remote-audio", stream, true);
        return;
      }

      if (cameraSlot) {
        const resolvedTrack =
          track?.kind === "video"
            ? track
            : stream?.getVideoTracks?.()[0] || null;

        if (resolvedTrack) {
          if (receiver?.getStats) {
            startReceiverFpsMonitor(cameraSlot, receiver);
          } else {
            stopReceiverFpsMonitor(cameraSlot);
          }
          mediaSinkRegistry.setTrack(cameraSlot, resolvedTrack);
        } else {
          stopReceiverFpsMonitor(cameraSlot);
          console.warn("[AdRemoteClient] Missing video track for camera slot", {
            cameraSlot,
            mid: mid || null,
            streamId: stream?.id ?? null,
          });
        }
      }
    };

    socket.registerEvent("onTrack", onTrackHandler, null);
  }, [
    config,
    mediaSinkRegistry,
    socket,
    startReceiverFpsMonitor,
    stopReceiverFpsMonitor,
  ]);
}
